import { useEffect } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { FADE_TRANSITION_MS } from '../config';
import { Fallback } from '../player/Fallback';
import { MenuClassic } from './MenuClassic';
import { MenuGrid } from './MenuGrid';
import { PromoBurst } from './PromoBurst';
import type {
  ManifestItem,
  ManifestMenuBoardItem,
  ManifestPromoItem,
} from '../types';

type TemplateItem = ManifestMenuBoardItem | ManifestPromoItem;

// Kind-specific registries — keeps each component's prop type narrow.
//
// M2E.D.2: menu-classic + menu-grid registered for MENU_BOARD;
// promo-burst registered for PROMO. M2E.E will add additional
// templates beyond these three.
const MENU_BOARD_TEMPLATES: Record<
  string,
  React.FC<{ item: ManifestMenuBoardItem; onEnd: () => void }>
> = {
  'menu-classic': MenuClassic,
  'menu-grid': MenuGrid,
};

const PROMO_TEMPLATES: Record<
  string,
  React.FC<{ item: ManifestPromoItem; onEnd: () => void }>
> = {
  'promo-burst': PromoBurst,
};

function isKnownTemplate(item: ManifestItem): boolean {
  if (item.itemKind === 'MENU_BOARD') {
    return item.templateKey in MENU_BOARD_TEMPLATES;
  }
  if (item.itemKind === 'PROMO') {
    return item.templateKey in PROMO_TEMPLATES;
  }
  return false;
}

// Returns true for any item the Player can render right now: MEDIA items
// (always — handled by ItemView), and non-MEDIA items whose templateKey
// is in one of the registries above. Items that fail this check are
// filtered out by Player.tsx at adopt time so they never reach Loop.
//
// Legacy snapshot rows pre-B.6c lack `itemKind` and are treated as MEDIA
// (forward compat with versions written before the discriminator landed).
export function isRenderableItem(item: ManifestItem): boolean {
  const kind = (item as { itemKind?: string }).itemKind;
  if (kind === undefined || kind === 'MEDIA') return true;
  return isKnownTemplate(item);
}

const NOOP = (): void => undefined;

interface TemplateViewProps {
  item: TemplateItem;
  fadingOut?: boolean;
  onEnd: () => void;
}

// Dispatches a non-MEDIA manifest item to its registered template
// component. The fadingOut prop drives an opacity transition matching
// ItemView's existing fade pattern; when fading the inner template's
// onEnd is swapped for a stable no-op so it can't re-trigger advance
// from the outgoing layer.
export function TemplateView({
  item,
  fadingOut = false,
  onEnd,
}: TemplateViewProps) {
  const effectiveOnEnd = fadingOut ? NOOP : onEnd;

  if (item.itemKind === 'MENU_BOARD') {
    const Cmp = MENU_BOARD_TEMPLATES[item.templateKey];
    return (
      <FadeLayer fadingOut={fadingOut}>
        {Cmp ? (
          <Cmp item={item} onEnd={effectiveOnEnd} />
        ) : (
          <UnknownTemplate
            kind="MENU_BOARD"
            templateKey={item.templateKey}
            onEnd={effectiveOnEnd}
          />
        )}
      </FadeLayer>
    );
  }

  // item.itemKind === 'PROMO'
  const Cmp = PROMO_TEMPLATES[item.templateKey];
  return (
    <FadeLayer fadingOut={fadingOut}>
      {Cmp ? (
        <Cmp item={item} onEnd={effectiveOnEnd} />
      ) : (
        <UnknownTemplate
          kind="PROMO"
          templateKey={item.templateKey}
          onEnd={effectiveOnEnd}
        />
      )}
    </FadeLayer>
  );
}

// Wraps a template in an absolutely-positioned div that handles the fade
// transition. Templates themselves don't need to know about fading.
function FadeLayer({
  fadingOut,
  children,
}: {
  fadingOut: boolean;
  children: ReactNode;
}) {
  const style: CSSProperties = {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    opacity: fadingOut ? 0 : 1,
    transition: `opacity ${FADE_TRANSITION_MS}ms ease-in-out`,
  };
  return <div style={style}>{children}</div>;
}

// Defense-in-depth: shown if isRenderableItem missed an item somehow.
// Logs once and advances after a short delay so the loop doesn't stall.
function UnknownTemplate({
  kind,
  templateKey,
  onEnd,
}: {
  kind: string;
  templateKey: string;
  onEnd: () => void;
}) {
  useEffect(() => {
    // eslint-disable-next-line no-console
    console.warn(
      `[player] TemplateView: no renderer for ${kind} templateKey ` +
        `'${templateKey}', advancing in 1s`,
    );
    const t = window.setTimeout(onEnd, 1000);
    return () => clearTimeout(t);
  }, [kind, templateKey, onEnd]);

  return <Fallback message={`Unsupported template: ${templateKey}`} />;
}
